import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import Text from '@/components/Typography/Text';
import PetWaitingIcon from '@/components/Pets/PetWaitingIcon';
import { getPetById, PET_IDS } from '@/constants/pets';
import { useAuthStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';

export const WORK_STARTUP_PHRASES = [
  'I’ll get started on that right away. Let me boot up my computer.',
  'Just getting my workspace ready for you.',
  'One moment while my computer wakes up.',
  'Thanks for waiting. I’m still getting ready.',
];
const PHRASE_INTERVAL = 7000;
const phasePhrases = {
  connecting: 'I’m connecting to your Mac so I can get started.',
  opening: 'All set. I’m opening our chat.',
  confirming: 'I’m checking that my computer is ready.',
  resuming: 'Let me wake up my computer so we can pick up where we left off.',
  service: 'I’ve saved your request. I’m waiting for my computer to be available, then I’ll get started.',
  deployment: 'My computer is getting an update. I’ve saved your request and I’ll start as soon as it’s ready.',
  credits: 'I’ve saved your request. Add some credits and I’ll get started automatically.',
};

export default function WorkStartupCompanion({ phase = 'starting', startedAt = null }) {
  const { colors } = useTheme();
  const selectedPetId = useAuthStore(state => state.user?.preferences?.appearance?.selectedPet);
  const pet = getPetById(selectedPetId) || getPetById(PET_IDS.PIXEL_SAGE);
  const [mountedAt] = useState(Date.now);
  const [now, setNow] = useState(Date.now);
  const index = Math.min(WORK_STARTUP_PHRASES.length - 1,
    Math.max(0, Math.floor((now - (startedAt || mountedAt)) / PHRASE_INTERVAL)));
  const phrase = phasePhrases[phase] || WORK_STARTUP_PHRASES[index];

  useEffect(() => {
    if (phasePhrases[phase] || index === WORK_STARTUP_PHRASES.length - 1) return undefined;
    const timer = setTimeout(() => setNow(Date.now()),
      Math.max(1, (startedAt || mountedAt) + (index + 1) * PHRASE_INTERVAL - Date.now()));
    return () => clearTimeout(timer);
  }, [phase, index, mountedAt, startedAt]);

  return <View testID="work-startup-companion" style={styles.row}>
    <PetWaitingIcon size={32} defaultPetId={PET_IDS.PIXEL_SAGE} />
    <View style={styles.speechColumn}>
      <Text size="xs" color="secondary" weight="semibold" style={styles.name}>{pet.name}</Text>
      <View accessible accessibilityLiveRegion="polite" accessibilityLabel={`${pet.name}: ${phrase}`}
        style={[styles.bubble, { backgroundColor: colors.solid_color_secondary }]}>
        <View style={[styles.tail, { backgroundColor: colors.solid_color_secondary }]} />
        <Text size="m" style={styles.phrase}>{phrase}</Text>
      </View>
    </View>
  </View>;
}

WorkStartupCompanion.propTypes = { phase: PropTypes.string, startedAt: PropTypes.number };

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, maxWidth: 560, width: '100%' },
  speechColumn: { flex: 1, minWidth: 0, alignItems: 'flex-start' },
  name: { marginLeft: 4, marginBottom: 7 },
  bubble: { borderRadius: 18, paddingHorizontal: 18, paddingVertical: 14, maxWidth: '100%' },
  tail: { position: 'absolute', width: 12, height: 12, left: -5, top: 20, transform: [{ rotate: '45deg' }] },
  phrase: { lineHeight: 23, flexShrink: 1 },
});
